import React, { useState, useEffect, ChangeEvent, useRef } from "react";
import "./component.css";
import { ADD_DOC_FILE, GET_ALL_ACCESSIBLE_DOCS } from "../utils/constants";
import {
  makeAuthenticatedGet,
  makeAuthenticatedPostForFile,
} from "../utils/fetchUtils";
import { useNavigate } from "react-router-dom";
import { DocData } from "../utils/types";
import uploadImg from "../assets/upload-img.svg";
import or from "../assets/or.svg";

interface DocAccessibilityFormProps {
  guestEmail?: string;
  onGuestEmailRequired?: () => void;
}

const DocAccessibilityForm = ({
  guestEmail,
  onGuestEmailRequired,
}: DocAccessibilityFormProps) => {
  const navigate = useNavigate();

  // react states
  const [formData, setFormData] = useState<DocData>({
    outputFormat: "DOCX",
    file: null,
    tag: "",
    fileName: "",
  });
  const [isDirty, setIsDirty] = useState(true);
  const [fileName, setFileName] = useState("");
  const [fileAnnouncement, setFileAnnouncement] = useState("");
  const [allTags, setAllTags] = useState<any[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const fileAnnouncementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  //drag to upload
  const [dragging, setDragging] = useState(false);

  const announceSelectedFile = (selectedFileName: string) => {
    if (fileAnnouncementTimeoutRef.current) {
      clearTimeout(fileAnnouncementTimeoutRef.current);
    }

    setFileAnnouncement("");
    fileAnnouncementTimeoutRef.current = setTimeout(() => {
      setFileAnnouncement(`File selected: ${selectedFileName}`);
    }, 200);
  };

  // Handlers for drag events
  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (e.dataTransfer.files.length > 1) {
      alert("Please drag and drop only one file at a time.");
      return;
    }

    const droppedFile = e.dataTransfer.files[0]; // Get the first file
    if (droppedFile) {
      setFileName(droppedFile.name);
      announceSelectedFile(droppedFile.name);
      setFormData({
        ...formData,
        file: droppedFile,
        fileName: droppedFile.name,
      });
    }
  };

  // const handleOptionChange = (event: any) => {
  //   setSelectedOption(event.target.value);
  //   handleChange(event);
  // };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });

  };

  const fetchDocs = async () => {
    try {
      const url = GET_ALL_ACCESSIBLE_DOCS;
      const response = await makeAuthenticatedGet(url);
      const temp = response;
      const tags: any[] = [];
      temp.documents.forEach((doc: any) => {
        tags.push(doc.tag);
      });
      setAllTags(Array.from(new Set(tags.filter(Boolean))));
    } catch (error: any) {
      if (error.response && error.response.data)
        console.error("Error fetching books: ", error);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setFileName(file.name);
      announceSelectedFile(file.name);
      setFormData({
        ...formData,
        file: files ? files[0] : null,
        fileName: file.name,
      });
    } else {
      setFileName(event.target.value);
      setFormData({
        ...formData,
        fileName: event.target.value,
      });
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const isGuest = !localStorage.getItem("token");

    if (isSubmittingRef.current) return;

    if (isGuest && !guestEmail) {
      onGuestEmailRequired?.();
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const data = new FormData();
    // data.append("outputFormat", formData.outputFormat);
    data.append("tag", formData.tag);
    data.append("output_format", formData.outputFormat);
    if (formData.file) {
      data.append("file", formData.file);
    }
    if (isGuest && guestEmail) {
      data.append("email", guestEmail);
    }

    // API call
    console.log("Form data:", formData);
    const url = ADD_DOC_FILE;

    try {
      setSubmitError("");
      const response = await makeAuthenticatedPostForFile(url, data);
      console.log(response);
      navigate("/docaccess/uploading", {
        state: { deliveryEmail: isGuest ? guestEmail : undefined },
      });
    } catch (error) {
      console.error("Error submitting document:", error);
      setSubmitError("We couldn’t submit your document. Please try again.");
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  //use Effects

  useEffect(() => {
    const isNotDirty = formData.outputFormat !== "" && formData.file != null;
    setIsDirty(!isNotDirty);
  }, [formData]);

  useEffect(() => {
    fetchDocs();
  }, []);

  useEffect(() => {
    return () => {
      if (fileAnnouncementTimeoutRef.current) {
        clearTimeout(fileAnnouncementTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <form
        id="document-conversion-form"
        className="doc-form upload-doc-form"
        onSubmit={handleSubmit}
        aria-busy={isSubmitting}
      >
        <p
          className="visually-hidden"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {fileAnnouncement}
        </p>
        {submitError && <p className="page-error" role="alert">{submitError}</p>}
        {isSubmitting && (
          <p
            id="document-submit-status"
            className="document-submit-status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Submitting your document. Please wait.
          </p>
        )}
        {guestEmail && (
          <div className="guest-delivery-email" role="status" aria-live="polite">
            <p>
              Converted file will be sent to <strong>{guestEmail}</strong>.
            </p>
            <button
              type="button"
              className="click"
              onClick={onGuestEmailRequired}
              aria-label="Change delivery email"
            >
              Change email
            </button>
          </div>
        )}
        {fileName !== "" && (
          <div
            className="d-flex w-[90%] doc-upload-alert mb-5"
          >
            <p>File selected: {fileName}</p>
          </div>
        )}
        <div className="mb-3 input-div">
          <h2 id="formFileLabel" className="form-label">
            {formData.file === null ? "Document Upload" : "Document Selected"}
          </h2>
          <p>Add your documents here</p>
          <section
            className={`upload-input-container ${dragging ? "is-dragging" : ""}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            aria-label="Drag and drop your file here or click to browse"
          >
            <img src={uploadImg} alt="" aria-hidden="true" />
            <p style={{ fontSize: "1rem" }}>
              Drag your file to start uploading
            </p>
            <p className="sr-only">OR</p>
            <img src={or} alt="" />
            <input
              className="form-control input-1"
              type="file"
              id="formFile"
              onChange={handleFileChange}
              name="file"
              accept=".pdf"
              aria-describedby="fileHelp"
              aria-labelledby="formFileLabel"
              required
            />
            <button 
            className="upload-button" 
            type="button"
            onClick={() => document?.getElementById('formFile')?.click()}
            >
              {fileName === "" ? "Browse files" : "Change File"}
            </button>
            <p
              className="filename"
              style={{ fontSize: "0.75rem", padding: "0", margin: "0" }}
            >
              {fileName}
            </p>
          </section>
          <p id="fileHelp">Supported input format: PDF. Maximum file size: 300 MB.</p>
        </div>
        <div className="input-container">
          <div className="mb-3 input-div half-width">
            <label htmlFor="tag" id="tag-label" className="form-label">
              Tag (optional)
            </label>
            <div className="tag-dropdown">
              <p id="tagInstructions">
                Choose an existing tag or type a new one to organize this request.
              </p>
                <input
                  className="downshift-input form-control input-4"
                  id="tag"
                  list="tag-options"
                  aria-describedby="tagInstructions"
                  name="tag"
                  placeholder="Select or enter a tag"
                  value={formData.tag}
                  onChange={handleChange}
                  autoComplete="off"
                />
                <datalist id="tag-options">
                  {allTags.map((tag) => <option value={tag} key={tag} />)}
                </datalist>
            </div>
          </div>

          <div className="mb-3 input-div half-width">
            <label htmlFor="output-format" id="output-format-label" className="form-label">
              Select output format *
            </label>
            <select
              style={{ marginTop: "2.8rem", borderRadius: "12px" }}
              className="form-control input-3"
              id="output-format"
              name="outputFormat"
              value={formData.outputFormat}
              onChange={handleChange}
              required
              aria-required="true"
            >
              <option value="" disabled hidden>
                Select output format
              </option>
              <option value="HTML">HTML</option>
              <option value="DOCX">DOCX</option>
            </select>
          </div>
        </div>
        <div
          style={{ width: "100%", paddingTop: "2rem" }}
          className="d-flex items-center justify-center"
        >
          <button
            type="submit"
            className="doc-submit"
            disabled={isDirty || isSubmitting}
            aria-describedby={
              isDirty
                ? "disabledReason"
                : isSubmitting
                  ? "document-submit-status"
                  : undefined
            }
          >
            {isSubmitting ? "Submitting document…" : "Convert a Document"}
          </button>
          <p
            id="disabledReason"
            style={{ fontSize: "0.75rem" }}
            className="sr-only"
          >
            Please fill out all required fields to enable the button.
          </p>
        </div>
      </form>
    </>
  );
};

export default DocAccessibilityForm;
